// Work Queue utilities for Maris Desk

// Category tags with "why" explanations
export const CATEGORY_TAGS = {
  URGENT: {
    label: 'Urgent issue',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: 'AlertTriangle'
  },
  TIME_SENSITIVE: {
    label: 'Time-sensitive',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: 'Clock'
  },
  INFO_REQUEST: {
    label: 'Info request',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: 'HelpCircle'
  },
  SALES: {
    label: 'Sales inquiry',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: 'DollarSign'
  },
  OTHER: {
    label: 'Other',
    color: 'bg-stone-100 text-stone-700 border-stone-200',
    icon: 'MessageSquare'
  }
};

// Auto-categorize message based on content and priority
export function categorizeMessage(message, subject = '') {
  const text = (message?.bodyText || '').toLowerCase() + ' ' + (subject || '').toLowerCase();
  const priority = message?.priority || 'NORMAL';
  
  // Check for urgent issues
  if (priority === 'URGENT' || 
      text.includes('urgent') ||
      text.includes('emergency') ||
      text.includes('help') && (text.includes('check-in') || text.includes('checking in')) ||
      text.includes('no hot water') ||
      text.includes('locked out') ||
      text.includes('can\'t access') ||
      text.includes('not working') ||
      text.includes('broken')) {
    return {
      category: 'URGENT',
      why: getWhyText('URGENT', text)
    };
  }
  
  // Check for time-sensitive requests
  if (text.includes('late checkout') ||
      text.includes('early check-in') ||
      text.includes('transfer') ||
      text.includes('tomorrow') ||
      text.includes('today') ||
      text.includes('arriving') ||
      priority === 'SOON') {
    return {
      category: 'TIME_SENSITIVE',
      why: getWhyText('TIME_SENSITIVE', text)
    };
  }
  
  // Check for sales inquiries
  if (text.includes('availability') ||
      text.includes('pricing') ||
      text.includes('price') ||
      text.includes('rate') ||
      text.includes('book') ||
      text.includes('inquiry') ||
      text.includes('interested') ||
      text.includes('dates') ||
      text.includes('january') ||
      text.includes('february') ||
      text.includes('march') ||
      text.includes('april')) {
    return {
      category: 'SALES',
      why: getWhyText('SALES', text)
    };
  }
  
  // Check for info requests
  if (text.includes('wifi') ||
      text.includes('wi-fi') ||
      text.includes('password') ||
      text.includes('direction') ||
      text.includes('where') ||
      text.includes('how') ||
      text.includes('what') ||
      text.includes('pool') ||
      text.includes('parking') ||
      text.includes('check-out time')) {
    return {
      category: 'INFO_REQUEST',
      why: getWhyText('INFO_REQUEST', text)
    };
  }
  
  return {
    category: 'OTHER',
    why: 'General inquiry'
  };
}

function getWhyText(category, text) {
  switch (category) {
    case 'URGENT':
      if (text.includes('check-in') || text.includes('checking in')) return 'check-in access issue';
      if (text.includes('hot water')) return 'no hot water reported';
      if (text.includes('wifi') || text.includes('wi-fi')) return 'connectivity issue';
      if (text.includes('room') && text.includes('ready')) return 'room not ready';
      if (text.includes('locked') || text.includes('access')) return 'access issue';
      return 'requires immediate attention';
    
    case 'TIME_SENSITIVE':
      if (text.includes('late checkout')) return 'late checkout request';
      if (text.includes('early check-in')) return 'early check-in request';
      if (text.includes('transfer')) return 'transfer arrangement needed';
      if (text.includes('tomorrow') || text.includes('today')) return 'time-bound request';
      return 'time-sensitive request';
    
    case 'SALES':
      if (text.includes('availability') || text.includes('dates')) return 'availability + pricing';
      if (text.includes('book') || text.includes('interested')) return 'booking inquiry';
      if (text.includes('group')) return 'group booking inquiry';
      return 'availability inquiry';
    
    case 'INFO_REQUEST':
      if (text.includes('wifi') || text.includes('wi-fi')) return 'Wi-Fi / connectivity';
      if (text.includes('direction') || text.includes('where')) return 'directions / location';
      if (text.includes('pool')) return 'pool / amenities';
      if (text.includes('parking')) return 'parking info';
      return 'information request';
    
    default:
      return 'general inquiry';
  }
}

// Calculate SLA timer from message timestamp
export function calculateSLATimer(messageCreatedAt) {
  if (!messageCreatedAt) return null;
  
  const now = new Date();
  const created = new Date(messageCreatedAt);
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 0) return null;
  
  if (diffMins < 60) {
    return {
      text: `Waiting ${diffMins}m`,
      isOverdue: diffMins > 30,
      minutes: diffMins
    };
  }
  
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  
  return {
    text: `Waiting ${hours}h ${mins}m`,
    isOverdue: hours >= 1,
    minutes: diffMins
  };
}

// Escalation reasons
export const ESCALATION_REASONS = [
  { value: 'REFUND_BILLING', label: 'Refund / billing' },
  { value: 'SAFETY_SECURITY', label: 'Safety / security' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping' },
  { value: 'ACCESS_LOCK', label: 'Access / lock' },
  { value: 'OWNER_DECISION', label: 'Manager approval' },
  { value: 'OTHER', label: 'Other' }
];

// Escalation assign options
export const ESCALATION_ASSIGN_OPTIONS = [
  { value: 'OWNER', label: 'Owner' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'MANAGER', label: 'Manager' }
];

// Guest update scripts for escalation
export const GUEST_UPDATE_SCRIPTS = {
  default: "Hi there — thank you for flagging this. I'm escalating it now and will update you shortly.",
  withTime: "Hi there — thank you for flagging this. I'm escalating it now and will update you within 15 minutes."
};
